import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import * as PropTypes from 'prop-types'

import { cn } from "@/lib/utils"

/**
 * Tooltip primitives (Tooltip / TooltipTrigger / TooltipContent) — wrappers
 * around @radix-ui/react-tooltip.
 *
 * CONSUMER CONTRACT — aria-describedby wiring
 * --------------------------------------------
 * Radix renders TooltipContent in a portal and does NOT automatically wire
 * `aria-describedby` from the trigger to the content. TooltipContent is
 * announced reliably on hover, but NOT reliably on keyboard focus of the
 * trigger across all browser + screen reader combinations.
 *
 * When a Tooltip is used on a trigger whose visible label is insufficient on
 * its own — e.g., an icon-only button, a button whose label is truncated, or
 * any trigger that relies on the tooltip to carry non-redundant information —
 * the consumer MUST:
 *
 *   1. Give <TooltipContent> a stable `id` prop.
 *   2. Pass `aria-describedby={<that-id>}` to the trigger element (normally
 *      the child rendered inside <TooltipTrigger asChild>).
 *
 * Failure to do so renders the tooltip text unavailable to keyboard +
 * screen-reader users. This is a WCAG 2.1 AA compliance failure.
 *
 * If the tooltip's content merely restates the trigger's visible text or
 * existing aria-label, the wiring is OPTIONAL — screen readers already have
 * the information and the attribute would be redundant.
 *
 * Example (icon-only button — wiring required):
 *
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button size="icon" aria-label="Save" aria-describedby="save-tip">
 *         <SaveIcon />
 *       </Button>
 *     </TooltipTrigger>
 *     <TooltipContent id="save-tip">
 *       Saves your work to the cloud
 *     </TooltipContent>
 *   </Tooltip>
 *
 * A follow-up tech-debt item is tracked to add an ESLint rule enforcing this
 * contract at build time; see plans/2026-04-20-backlog-tooltip-aria-describedby-eslint.md.
 *
 * See brand-guide §15 (Accessibility Standards) and the Epic 7 TDD.
 */
const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 tooltip-container text-left leading-5 font-normal overflow-hidden rounded-lg bg-popover p-4 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

TooltipContent.propTypes = {
  className: PropTypes.string,
  sideOffset: PropTypes.number,
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
